<template>
  <div class="settings-tiers">
    <Loading v-if="is_loading" />

    <div class="tiers-empty" v-else-if="tiers.length === 0">
      <div class="tiers-empty-card">
        <h3>Create your first Tier</h3>
        <p>
          Creator tiers let you segment and reward creators based on their performance. Start by adding your first tier
          — we'll guide you through naming, designing, and setting conditions.
        </p>
        <p class="blue">
          Tips: You'll need a default tier — it's where all new creators start automatically. We'll prompt you to set
          one up on your first tier.
        </p>
        <div class="btn-box">
          <Button size="32" color="dark-blue" :border_r="6" @click="openEditor">Create</Button>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="tiers-draft-bar" v-if="has_draft">
        <div class="tiers-draft-text">
          <strong>You have unpublished draft tiers</strong>
          <span>Creators cannot see draft tiers until you publish them.</span>
        </div>
        <Button size="40" color="dark-blue" :border_r="6" @click="is_publish_show = true">Publish updates</Button>
      </div>

      <div class="tiers-toolbar">
        <span class="tiers-rank-by">Rank by: highest to lowest</span>
        <!-- always available: at the cap the label just stops offering to add -->
        <Button color="black-90" size="32" :style="'border'" :border_r="6" @click="openEditor">
          {{ is_full ? 'Edit tier' : 'Edit and add tier' }}
        </Button>
      </div>

      <Table class="tiers-table">
        <template #head>
          <tr>
            <th class="col-rank"><span>Rank</span></th>
            <th class="col-tier"><span>Tier</span></th>
            <th class="col-status"><span>Status</span></th>
            <th class="col-condition"><span>Condition</span></th>
            <th class="col-creators"><span>Creators</span></th>
            <th class="col-edited"><span>Last Updated</span></th>
          </tr>
        </template>

        <template #body>
          <!-- the editor works on the whole set, so any row opens it -->
          <tr v-for="row in rows" :key="row.tier.key" class="tiers-row" @click="openEditor">
            <td class="col-rank">{{ row.tier.rank }}</td>
            <td class="col-tier"><TierBadge :tier="row.tier" :size="24" /></td>
            <td class="col-status">
              <span :class="['tiers-status', row.is_draft && 'is-draft']">
                <i class="tiers-status-dot"></i>{{ row.is_draft ? 'Draft' : 'Active' }}
              </span>
            </td>
            <td class="col-condition">
              <span class="tiers-starting" v-if="row.is_starting">Starting Tier</span>
              <!-- a draft may be saved without conditions, but it cannot be published that way -->
              <span class="tiers-no-conditions" v-if="!row.texts.length">
                <SvgIcon name="warning" />No conditions set
              </span>
              <ul class="tiers-condition-list" v-else>
                <li v-for="(text, ti) in row.texts" :key="ti">{{ text }}</li>
              </ul>
            </td>
            <td class="col-creators">
              {{ row.tier.creators_count === null ? 'Calculating...' : row.tier.creators_count }}
            </td>
            <td class="col-edited">
              <span>{{ formatEdited(row.tier.last_edited) }}</span>
            </td>
          </tr>
        </template>
      </Table>
    </template>

    <TierEditDialog :is_show="is_form_show" :value="tiers" :is_saving="is_saving" @close="closeForm" @save="onSave" />

    <TierConfirm
      :is_show="is_publish_show"
      title="Are you sure you want to confirm the changes to the Tier?"
      :lines="publish_lines"
      :is_busy="is_publishing"
      @cancel="is_publish_show = false"
      @confirm="publishAll"
    />
  </div>
</template>

<script>
import moment from 'moment';
import Button from '@/components/ui/Button.vue';
import Loading from '@/components/ui/Loading.vue';
import Table from '@/components/ui/Table.vue';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import { useNotification } from '@/components/ui/notification';
import TierBadge from './TierBadge.vue';
import TierConfirm from './TierConfirm.vue';
import TierEditDialog from './TierEditDialog.vue';
import {
  hasUnpublishedChanges,
  isStartingTier,
  MAX_TIERS,
  SAVE_DRAFT_URL,
  SAVE_PUBLISH_URL,
  sortTiersByRank,
  tierConditionTexts,
  tierFromApi,
  tierListQuery,
  tiersToFormData,
  TIER_STATUS,
} from './tierModel';

// the tier endpoints all take multipart, and the axios instance defaults to json
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export default {
  name: 'Tiers',

  components: {
    Button,
    Loading,
    Table,
    SvgIcon,
    TierBadge,
    TierConfirm,
    TierEditDialog,
  },

  setup() {
    const { openNotBottom } = useNotification();
    return { openNotBottom };
  },

  data() {
    return {
      tiers: [],
      is_loading: true,
      is_saving: false,
      is_publishing: false,
      is_form_show: false,
      is_publish_show: false,
    };
  },

  computed: {
    rows() {
      return this.tiers.map((tier) => ({
        tier,
        texts: tierConditionTexts(tier),
        is_draft: tier.status === TIER_STATUS.DRAFT,
        is_starting: isStartingTier(tier),
        has_unpublished: hasUnpublishedChanges(tier),
      }));
    },

    // only an actual draft needs publishing, so a list that is all Active hides the bar
    has_draft() {
      return this.rows.some((row) => row.is_draft);
    },

    draft_tiers() {
      return this.rows.filter((row) => row.is_draft).map((row) => row.tier);
    },

    is_full() {
      return this.tiers.length >= MAX_TIERS;
    },

    publish_lines() {
      return this.draft_tiers.map((tier) => `${tier.name.trim() || `Rank ${tier.rank}`} Tier will go live`);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.is_loading = true;
      // tiers cap at MAX_TIERS, so one page holds them all and the ranking stays complete
      const params = tierListQuery({ per_page: 100, sort_key: 'rank', sort_val: 'asc' });

      this.$ajax
        .get('/tier/list', { params })
        .then((res) => {
          const rows = res.data?.data?.data ?? res.data?.data ?? [];
          this.tiers = sortTiersByRank((Array.isArray(rows) ? rows : []).map(tierFromApi));
        })
        .catch((err) => {
          console.log(err);
          this.openNotBottom('Could not load tiers.', 'error');
        })
        .finally(() => {
          this.is_loading = false;
        });
    },

    formatEdited(value) {
      if (!value) return '—';
      // the API sends "YYYY-MM-DD HH:mm:ss", which Safari's Date constructor rejects
      const date = moment(value, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601]);
      return date.isValid() ? date.format('MMM D. YYYY') : '—';
    },

    openEditor() {
      this.is_form_show = true;
    },

    closeForm() {
      this.is_form_show = false;
    },

    /**
     * The editor hands back the whole set, which is exactly what the batch endpoints take:
     * one request, one entry per tier. Rank is not sent, it comes from the submission order,
     * so the rows go out already sorted rank 1 first with the default tier last.
     *
     * Rows the editor dropped simply stop being submitted.
     */
    onSave(next_tiers) {
      if (this.is_saving) return;
      this.is_saving = true;

      const published = next_tiers.some((tier) => tier.status === TIER_STATUS.PUBLISHED);
      const url = published ? SAVE_PUBLISH_URL : SAVE_DRAFT_URL;
      // read before the request: this still holds the set the editor opened with, and a
      // non-empty one means the editor was in edit mode rather than creating a first set
      const is_edit = this.tiers.length > 0;
      const message = !published ? 'Tiers saved as draft.' : is_edit ? 'Tiers updated.' : 'Tiers published.';

      this.$ajax
        .post(url, tiersToFormData(sortTiersByRank(next_tiers)), MULTIPART)
        .then(() => {
          this.openNotBottom(message, 'success');
          this.closeForm();
          // reload so ids, ranks and timestamps come from the server rather than being guessed
          this.load();
        })
        .catch((err) => {
          console.log(err);
          this.openNotBottom('Could not save the tiers.', 'error');
        })
        .finally(() => {
          this.is_saving = false;
        });
    },

    // publishing from the list sends the set as it stands, which flips every draft live
    publishAll() {
      if (this.is_publishing) return;
      this.is_publishing = true;

      this.$ajax
        .post(SAVE_PUBLISH_URL, tiersToFormData(sortTiersByRank(this.tiers)), MULTIPART)
        .then(() => {
          this.openNotBottom('Tiers published.', 'success');
        })
        .catch((err) => {
          console.log(err);
          this.openNotBottom('Could not publish the tiers.', 'error');
        })
        .finally(() => {
          this.is_publishing = false;
          this.is_publish_show = false;
          this.load();
        });
    },
  },
};
</script>
<style lang="scss">
.settings-tiers {
  .tiers-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 260px);
    background: var(--bg-10);
    margin-top: 16px;
  }

  .tiers-empty-card {
    width: 100%;
    max-width: 620px;
    margin: 0 32px;
    text-align: center;

    h3 {
      font: var(--font-med-24);
      color: var(--black-90);
      margin-bottom: 24px;
    }

    p {
      color: var(--black-90);
      max-width: 330px;
      margin: 16px auto;

      &.blue {
        color: var(--blue);
      }
    }

    .btn-box {
      margin-top: 32px;
      display: flex;
      justify-content: center;

      .ui-button {
        width: 280px;
      }
    }
  }

  .tiers-draft-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    background: var(--light-yellow);
    margin: 16px 0 0;

    .tiers-draft-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      color: var(--black-90);

      strong {
        font: var(--font-med-16);
      }
    }
  }

  .tiers-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 16px 0;

    .ui-button {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      svg {
        width: 12px;
        height: 12px;
      }
    }
  }

  .tiers-rank-by {
    color: var(--black-50);
    font: var(--font-med-12);
  }
}

// ui-table already supplies the head background, alignment and colours
.tiers-table {
  table-layout: fixed;

  th {
    font: var(--font-med-14);
    padding: 8px 16px;
  }

  td {
    padding: 8px 16px;
    vertical-align: middle;
    border-bottom: 1px solid var(--black-20);
  }

  // fixed layout, so each width has to clear its own header text plus the 16px side padding.
  // condition has no width and soaks up everything the others give back.
  .col-rank {
    width: 56px;
  }

  .col-tier {
    width: 120px;
  }

  .col-status {
    width: 80px;
  }

  .col-creators {
    width: 90px;
  }

  .col-edited {
    width: 112px;
  }

  td.col-rank {
    font: var(--font-med-16);
  }

  .tiers-row {
    cursor: pointer;

    &:hover {
      background: var(--bg-10);
    }
  }

  .tiers-status {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;

    .tiers-status-dot {
      width: 8px;
      height: 8px;
      flex: 0 0 auto;
      border-radius: 50%;
      background: var(--green);
    }

    &.is-draft .tiers-status-dot {
      background: var(--black-40);
    }
  }

  .tiers-starting {
    display: block;
    color: var(--black-50);
    margin-bottom: 4px;
  }

  .tiers-no-conditions {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--alert);

    svg {
      width: 14px;
      height: 14px;
      flex: 0 0 auto;
      fill: var(--yellow);
    }
  }

  .tiers-condition-list {
    margin: 0;
    padding-left: 18px;

    li {
      list-style: disc;
      color: var(--black-75);
      line-height: 22px;
    }
  }

  td.col-edited {
    color: var(--black-90);
  }
}
</style>
